export interface JarvisRunSpecValidationError {
  path: string;
  message?: string;
  keyword?: string;
}

export interface JarvisRunSpecValidationReport {
  valid: boolean;
  schemaVersion: string;
  schemaId?: string;
  generatedAt: string;
  errorCount: number;
  errors: JarvisRunSpecValidationError[];
}

export interface JarvisRunSpecResponse {
  spec: unknown;
  validated: boolean;
  validationReport: JarvisRunSpecValidationReport;
  schemaVersion?: string;
  schemaId?: string;
}

export interface JarvisRunSpecErrorResponse {
  errorCode?: string;
  message?: string;
  remediationSteps?: string[];
  missingReadyItems?: string[];
  validationReport?: JarvisRunSpecValidationReport;
  spec?: unknown;
}

export interface JarvisRunSpecRequest {
  ticketId: string;
  title?: string;
  priority?: string;
  owner?: string;
  workItemType?: string;
  tags?: string[];
}

export async function fetchJarvisRunSpec(
  projectId: string,
  params: JarvisRunSpecRequest,
): Promise<JarvisRunSpecResponse> {
  if (!projectId) throw new Error("projectId is required");
  if (!params?.ticketId) throw new Error("ticketId is required");

  const query = new URLSearchParams({
    ticket: params.ticketId,
  });

  if (params.title) query.set("title", params.title);
  if (params.priority) query.set("priority", params.priority);
  if (params.owner) query.set("owner", params.owner);
  if (params.workItemType) query.set("workItemType", params.workItemType);
  if (params.tags?.length) query.set("tags", params.tags.join(","));

  const response = await fetch(`/api/projects/${projectId}/jarvis/run-spec?${query.toString()}`);
  const payload = (await response.json()) as JarvisRunSpecResponse & JarvisRunSpecErrorResponse & {
    ok?: boolean;
    error?: string;
  };

  if (!response.ok) {
    const message = payload?.message ?? payload?.error ?? `Failed to export Jarvis run spec (status ${response.status})`;
    throw new JarvisRunSpecApiError(
      message,
      response.status,
      payload?.errorCode,
      payload?.remediationSteps ?? [],
      payload?.missingReadyItems ?? [],
      payload?.validationReport,
      payload?.spec,
    );
  }

  return payload;
}

export interface JarvisWorkItemRunSpecResponse {
  spec: unknown;
  validated: boolean;
  schemaVersion: string;
  schemaId?: string;
  validationReport: JarvisRunSpecValidationReport;
}

export class JarvisRunSpecApiError extends Error {
  status: number;
  errorCode?: string;
  remediationSteps: string[];
  missingReadyItems?: string[];
  validationReport?: JarvisRunSpecValidationReport;
  spec?: unknown;

  constructor(
    message: string,
    status: number,
    errorCode?: string,
    remediationSteps: string[] = [],
    missingReadyItems: string[] = [],
    validationReport?: JarvisRunSpecValidationReport,
    spec?: unknown,
  ) {
    super(message);
    this.status = status;
    this.errorCode = errorCode;
    this.remediationSteps = remediationSteps;
    this.missingReadyItems = missingReadyItems.length ? missingReadyItems : undefined;
    this.validationReport = validationReport;
    this.spec = spec;
  }
}

export async function fetchJarvisWorkItemRunSpec(
  workItemId: string,
): Promise<JarvisWorkItemRunSpecResponse> {
  if (!workItemId) throw new Error("workItemId is required");

  const response = await fetch(`/api/work-items/${workItemId}/jarvis-run-spec`);
  const payload = (await response.json()) as JarvisWorkItemRunSpecResponse & JarvisRunSpecErrorResponse;

  if (!response.ok) {
    const message = payload?.message ?? `Failed to export Jarvis run spec (status ${response.status})`;
    throw new JarvisRunSpecApiError(
      message,
      response.status,
      payload?.errorCode,
      payload?.remediationSteps ?? [],
      payload?.missingReadyItems ?? [],
      (payload as { validationReport?: JarvisRunSpecValidationReport })?.validationReport,
      payload.spec,
    );
  }

  return payload;
}
